/** This is a bundle offer search tile
 *  This tile will first check whether (1) the search performed are property search and (2) the result has been soldout
 *  Then it will render either the (1) Expansed or (2) Condensed search results.
 * */

import { FormattedMessage, useIntl } from 'react-intl'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { rem } from 'polished'
import useBestPriceBundleAndSavePackage from 'hooks/useBestPriceBundleAndSavePackage'
import BodyText from 'LuxKit/Typography/BodyText'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { sum } from 'lib/array/arrayUtils'
import { getBundleValueUp } from 'lib/bundleAndSave/getBundleValueUp'
import TextButton from 'LuxKit/Button/TextButton'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import Group from 'components/utils/Group'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import config from 'constants/config'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import { useOfferPrice } from 'hooks/Offers/useOfferPrice'
import { EmptyObject } from 'lib/object/objectUtils'

const Container = styled.div`
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  padding: ${rem(16)};
`

const StyledGroup = styled(Group)`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  > * + * {
    width: fit-content;
  }
`

interface Props {
  offer: App.Offer | App.OfferSummary
  bundleOffer: App.BundleOffer
  filters?: App.OfferListFilters
  offerLinkIncludesFilters?: boolean
}

function ViewBundleOfferContent({
  offer,
  bundleOffer,
  filters = EmptyObject,
  offerLinkIncludesFilters,
}: Props) {
  const intl = useIntl()
  const {
    bestPricePackage,
    bestPriceError,
    fetching,
    soldOut,
  } = useBestPriceBundleAndSavePackage(bundleOffer, offer.id, filters)

  const offerUrl = useOfferUrl(bundleOffer, {
    filters,
    bundledOfferId: offer.id,
    offerLinkIncludesFilters,
  })

  const priceMultiplier = (filters?.rooms?.length || 1)
  const extraGuestSurcharges = useMemo(() => bestPricePackage ?
      sum(bestPricePackage.packages.map(p =>
        getSumExtraGuestsSurcharge(filters.rooms || [], bundleOffer.bundledOffers[p.offerId], p),
      )) : 0, [bestPricePackage, filters.rooms, bundleOffer])

  const propertyFees = (bestPricePackage?.propertyFees || 0) * priceMultiplier

  const hotelPrice = (((bestPricePackage?.price ?? 0) + (bestPricePackage?.surcharge ?? 0)) * priceMultiplier) + extraGuestSurcharges
  const baseTotalPrice = hotelPrice + propertyFees

  const {
    saleUnit,
    total: totalPrice,
  } = useOfferPrice({
    value: bestPricePackage?.value,
    saleUnit: 'total',
    duration: bestPricePackage?.duration,
    total: baseTotalPrice,
  })

  if (!bestPricePackage || bestPriceError || fetching || soldOut) {
    return null
  }

  const secondOfferPackage = bestPricePackage.packages.find(x => x.offerId !== offer.id)
  const secondOffer = Object.values(bundleOffer.bundledOffers).find(x => x.id !== offer.id)

  const { shouldShowDiscountPercent, hotelDiscountPercent } = getBundleValueUp(bundleOffer, bestPricePackage)

  const linkParams = {
    to: offerUrl,
    ...(config.OPEN_NEW_TAB_OFFER_CLICK ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
  }

  return (
    <ErrorBoundary fallback={null} reportErrors={false}>
      <ClickableLink {...linkParams}>
        <Container>
          <StyledGroup direction="vertical" gap={8} fullHeight fullWidth>
            <Group direction="vertical" gap={8}>
              <div>
                <StructureLabel variant="promotion">{intl.formatMessage({
                  id: 'b+qes01uKv',
                  defaultMessage: 'Bundle & Save',
                  description: 'Label for bundle and save promotion on search tile',
                })}
                </StructureLabel>
              </div>
              {secondOfferPackage && (
                <BodyText variant="medium">
                  <FormattedMessage
                    id="YoRxmD3eB5"
                    defaultMessage="Great savings available when you bundle this hotel with a {duration, plural, one {# night} other {# nights}} stay at <b>{propertyName}</b>"
                    description="Bundle and save description showing the duration and property name of the second offer"
                    values={{
                      duration: secondOfferPackage.duration,
                      propertyName: secondOffer?.property.name,
                      b: (chunks) => <BodyText as="span" variant="medium" weight="bold">{chunks}</BodyText>,
                    }}
                  />
                </BodyText>
              )}
              <PriceRowPriceWithCaption
                size="S"
                caption={intl.formatMessage(
                  {
                    id: 'txEAg6DRUR',
                    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
                    description: 'Night count with from suffix on bundle offer search tile price',
                  },
                  { count: bestPricePackage.duration },
                )}
                price={totalPrice}
                saleUnit={saleUnit}
                discountPercentage={shouldShowDiscountPercent ? hotelDiscountPercent : undefined}
              />
            </Group>
            <Group direction="horizontal" horizontalAlign="end" fullWidth>
              <TextButton fit="mobile-full-width" kind="secondary">{intl.formatMessage({
                id: 'Ygo1PSwKps',
                defaultMessage: 'View bundle offer',
                description: 'CTA button to view the bundle and save offer details',
              })}
              </TextButton>
            </Group>
          </StyledGroup>
        </Container>
      </ClickableLink>
    </ErrorBoundary>
  )
}

export default ViewBundleOfferContent
